// RF-SLD20180014-0002 - Reconciliação mensal EDP
// Leitura dos ficheiros no browser, exportações e texto dos e-mails à EDP.
// O SGI não envia e-mails: prepara o anexo e o texto, e o responsável envia-os.
import moment from 'moment';
import * as XLSX from 'xlsx';
import { MAPEAMENTOS } from './colunas';
/**
 * Erro previsto da reconciliação, com mensagem para o utilizador (em português).
 * Distingue-se dos erros técnicos, cuja mensagem não se mostra.
 */
export class ErroReconciliacao extends Error {
    constructor(message) {
        super(message);
        this.name = 'ErroReconciliacao';
        Object.setPrototypeOf(this, ErroReconciliacao.prototype);
    }
}
/**
 * Mensagem a mostrar ao utilizador para um erro. Só passam as mensagens escritas para o
 * utilizador: erros da reconciliação no browser e respostas 400/409 do servidor (as
 * validações de gasReconciliacaoEdp.js). Erros técnicos (JavaScript, rede, servidor)
 * dão a mensagem por omissão, em português, e o detalhe vai para a consola.
 *
 * @param {any} e Erro apanhado
 * @param {string} padrao Mensagem por omissão
 * @returns {string} Mensagem em português
 */
export const mensagemErro = (e, padrao) => {
    if (e instanceof ErroReconciliacao)
        return e.message;
    const status = e?.status ?? e?.data?.error?.statusCode;
    const msg = e?.data?.error?.message;
    console.error(e);
    if (msg && (status === 400 || status === 409))
        return msg;
    if (status === 401 || status === 403)
        return 'Sem permissão para esta operação. Volte a entrar no SGI ou peça acesso.';
    return padrao;
};
export const DISCREPANCIA_LABEL = {
    falta_click: 'Falta no Click',
    falta_edp: 'Falta no ficheiro da EDP',
    tipo: 'Tipo de instalação diferente',
    valor: 'Valor diferente',
    repetido: 'Repetido no ficheiro',
    cobrado_local: 'Pago pela EDP e cobrado no local'
};
/**
 * Valor de célula como texto sem espaços nas pontas.
 *
 * @param {any} v Valor da célula
 * @returns {string} Texto (`''` para nulo)
 */
const limpar = (v) => (v === null || v === undefined ? '' : String(v).trim());
/**
 * Converte um valor de célula em número, aceitando o formato português («1.234,56 €»).
 *
 * @param {any} v Valor da célula
 * @returns {number|null} Número, ou `null` se vazio ou inválido
 */
export const lerValor = (v) => {
    if (v === null || v === undefined || v === '')
        return null;
    if (typeof v === 'number')
        return v;
    const s = String(v).replace(/[€\s]/g, '');
    // 1.234,56 → 1234.56 ; 1234.56 mantém
    const n = Number(s.includes(',') ? s.replace(/\./g, '').replace(',', '.') : s);
    return isNaN(n) ? null : n;
};
/**
 * CSV lido à mão: a `xlsx` 0.16 não deteta o separador «;», habitual nos CSV portugueses.
 * Deteta «;», «,» ou tabulação pela primeira linha, aceita aspas e lê UTF-8 ou Windows-1252.
 *
 * @param {ArrayBuffer} buffer Conteúdo do ficheiro
 * @returns {any[][]} Linhas do ficheiro, cada uma com as suas células (`null` se vazias)
 */
const lerCsv = (buffer) => {
    let texto = new TextDecoder('utf-8').decode(buffer);
    if (texto.includes('\uFFFD'))
        texto = new TextDecoder('windows-1252').decode(buffer);
    texto = texto.replace(/^\uFEFF/, '');
    const primeira = texto.split(/\r?\n/, 1)[0] || '';
    const sep = [';', '\t', ','].reduce((melhor, c) => (primeira.split(c).length > primeira.split(melhor).length ? c : melhor), ',');
    const rows = [];
    let row = [];
    let campo = '';
    let aspas = false;
    for (let i = 0; i < texto.length; i++) {
        const ch = texto[i];
        if (aspas) {
            if (ch === '"' && texto[i + 1] === '"') {
                campo += '"';
                i++;
            }
            else if (ch === '"')
                aspas = false;
            else
                campo += ch;
        }
        else if (ch === '"')
            aspas = true;
        else if (ch === sep) {
            row.push(campo);
            campo = '';
        }
        else if (ch === '\n' || ch === '\r') {
            if (ch === '\r' && texto[i + 1] === '\n')
                i++;
            row.push(campo);
            rows.push(row);
            row = [];
            campo = '';
        }
        else
            campo += ch;
    }
    if (campo !== '' || row.length > 0) {
        row.push(campo);
        rows.push(row);
    }
    return rows.map(r => r.map(c => (c === '' ? null : c)));
};
/**
 * Lê o ficheiro escolhido pelo utilizador.
 *
 * @param {File} file Ficheiro do input
 * @returns {Promise<ArrayBuffer>} Conteúdo do ficheiro
 */
const lerArrayBuffer = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new ErroReconciliacao('Não foi possível ler o ficheiro.'));
    reader.readAsArrayBuffer(file);
});
/**
 * Só as colunas indicadas da linha original (as que existirem no ficheiro).
 *
 * @param {Record<string, any>} original Linha original do ficheiro
 * @param {string[]} colunas Colunas a manter
 * @returns {Record<string, any>} Linha só com essas colunas
 */
const soColunas = (original, colunas) => Object.fromEntries(colunas.filter(c => c in original).map(c => [c, original[c]]));
/**
 * Lê um ficheiro (CSV, XLS ou XLSX) de uma fase e devolve as linhas normalizadas.
 * O cabeçalho é procurado nas primeiras 15 linhas (os ficheiros da EDP podem ter títulos).
 *
 * @param {File} file Ficheiro do input
 * @param {GasEdpFase} fase 1 = Click, 2 = EDP, 3 = ficheiro final (escolhe o mapeamento de colunas)
 * @returns {Promise<IGasEdpLinhaFicheiro[]>} Linhas com nº do parceiro ou ID Tarefa
 * @throws {Error} Se faltar alguma coluna obrigatória do mapeamento
 */
export const lerFicheiro = async (file, fase) => {
    const mapa = MAPEAMENTOS[fase];
    const buffer = await lerArrayBuffer(file);
    let rows;
    if (/\.csv$/i.test(file.name)) {
        rows = lerCsv(buffer);
    }
    else {
        let wb;
        try {
            wb = XLSX.read(new Uint8Array(buffer), { type: 'array', cellDates: true });
        }
        catch (e) {
            console.error(e);
            throw new ErroReconciliacao('O ficheiro não é um Excel (.xlsx/.xls) nem um CSV válido.');
        }
        const nomeFolha = mapa.folha && wb.SheetNames.includes(mapa.folha) ? mapa.folha : wb.SheetNames[0];
        rows = XLSX.utils.sheet_to_json(wb.Sheets[nomeFolha], { header: 1, defval: null, raw: true });
    }
    const obrigatorias = [mapa.numeroParceiro, mapa.tarefaId, mapa.tipoInstalacao, mapa.valor].filter(Boolean);
    const idxCabecalho = rows.slice(0, 15).findIndex(r => obrigatorias.every(c => (r || []).map(limpar).includes(c)));
    if (idxCabecalho === -1) {
        const primeira = (rows[0] || []).map(limpar);
        const faltam = obrigatorias.filter(c => !primeira.includes(c));
        throw new ErroReconciliacao(`Colunas em falta no ficheiro: ${faltam.join(', ')}.`);
    }
    const cabecalho = rows[idxCabecalho].map(limpar);
    const col = (nome) => (nome ? cabecalho.indexOf(nome) : -1);
    const iParceiro = col(mapa.numeroParceiro);
    const iTarefa = col(mapa.tarefaId);
    const iTipo = col(mapa.tipoInstalacao);
    const iValor = col(mapa.valor);
    return rows
        .slice(idxCabecalho + 1)
        .filter(r => r && r.some(c => limpar(c) !== ''))
        .map(r => {
        const dadosOriginais = {};
        cabecalho.forEach((h, i) => {
            if (!h)
                return;
            const v = r[i];
            dadosOriginais[h] = v instanceof Date ? moment(v).format('YYYY-MM-DD') : v;
        });
        return {
            numeroParceiro: iParceiro >= 0 ? limpar(r[iParceiro]) || null : null,
            tarefaId: iTarefa >= 0 ? limpar(r[iTarefa]) || null : null,
            tipoInstalacao: iTipo >= 0 ? limpar(r[iTipo]) || null : null,
            valor: iValor >= 0 ? lerValor(r[iValor]) : null,
            dadosOriginais
        };
    })
        .filter(l => (l.numeroParceiro || l.tarefaId) && (!mapa.incluir || mapa.incluir(l.dadosOriginais || {})))
        .map(l => (mapa.guardar ? { ...l, dadosOriginais: soColunas(l.dadosOriginais, mapa.guardar) } : l));
};
/**
 * Período em texto, para nomes e mensagens.
 *
 * @param {IGasReconciliacaoEdp} rec Cruzamento do período
 * @param {string} formato Formato das datas (moment)
 * @returns {string} «26/08/2026 a 25/09/2026»
 */
const periodoTexto = (rec, formato = 'DD/MM/YYYY') => `${moment(rec.periodo.from).format(formato)} a ${moment(rec.periodo.to).format(formato)}`;
/**
 * Nome do ficheiro exportado.
 *
 * @param {IGasReconciliacaoEdp} rec Cruzamento do período
 * @param {string} sufixo Identifica a exportação (ex.: «fase1-click»)
 * @returns {string} `gas-reconciliacao-edp-AAAA-MM-<sufixo>.xlsx`
 */
const nomeFicheiro = (rec, sufixo) => `gas-reconciliacao-edp-${moment(rec.periodo.to).format('YYYY-MM')}-${sufixo}.xlsx`;
/**
 * Processos com discrepâncias numa fase.
 *
 * @param {IGasReconciliacaoEdp} rec Cruzamento do período
 * @param {1|2} fase Fase das discrepâncias
 * @returns {IGasReconciliacaoEdpLinha[]} Processos com pelo menos uma discrepância nessa fase
 */
const comDiscrepancias = (rec, fase) => rec.processos.filter(p => (fase === 1 ? p.discrepancias.fase1 : p.discrepancias.fase2).length > 0);
/**
 * Exportação 1 — discrepâncias SGI × Click. Descarrega o XLSX no browser.
 *
 * @param {IGasReconciliacaoEdp} rec Cruzamento do período
 */
export const exportarFase1 = (rec) => {
    const header = ['Nº Processo SGI', 'Nº Processo Parceiro', 'ID Tarefa', 'Data Inspeção', 'Tipo Instalação SGI', 'Tipo Instalação Click', 'Discrepâncias'];
    const body = comDiscrepancias(rec, 1).map(p => [
        p.numeroProcesso,
        p.numeroParceiro ?? '',
        p.tarefaId ?? '',
        p.data ? moment(p.data).format('DD/MM/YYYY') : '',
        p.tipoInstalacao ?? '',
        p.click?.tipoInstalacao ?? '',
        p.discrepancias.fase1.map(d => DISCREPANCIA_LABEL[d]).join('; ')
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([header, ...body]), 'Discrepâncias Click');
    XLSX.writeFile(wb, nomeFicheiro(rec, 'fase1-click'));
};
/**
 * Descarrega um xlsx gerado no servidor, que o lbServices devolve em base64 em `$data`
 * (o mesmo que a exportação da listagem de processos). Usado na exportação da fase 2:
 * o ficheiro da EDP corrigido, gerado com o Carbone e com as linhas corrigidas pintadas.
 *
 * @param {any} resposta Resposta do método remoto (`$data` em base64)
 * @param {IGasReconciliacaoEdp} rec Cruzamento do período (para o nome do ficheiro)
 * @param {string} sufixo Identifica a exportação (ex.: «fase2-edp-corrigido»)
 */
export const descarregarXlsx = (resposta, rec, sufixo) => {
    const binario = atob(resposta.$data);
    const bytes = new Uint8Array(binario.length);
    for (let i = 0; i < binario.length; i++)
        bytes[i] = binario.charCodeAt(i);
    const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = nomeFicheiro(rec, sufixo);
    link.click();
    // Liberta o ficheiro da memória depois de o browser iniciar o download
    setTimeout(() => URL.revokeObjectURL(url), 1000);
};
/**
 * Tabela das correções da fase 2 para o corpo do e-mail, no formato que o IEP usa
 * (resposta de 27/08/2026: Processo, Processo_IEP, data fecho, Distrito, EDP_Tipo insta,
 * IEP_Tipo insta), com o valor quando há diferença de valor e a correção de cada linha.
 *
 * @param {IGasReconciliacaoEdpLinha[]} linhas Processos com discrepâncias na fase 2
 * @returns {string} Tabela em texto, uma linha por processo
 */
const tabelaCorrecoes = (linhas) => {
    const comValor = linhas.some(p => p.discrepancias.fase2.some(d => d === 'valor' || d === 'cobrado_local'));
    const cab = ['Processo', 'Processo_IEP', 'data fecho', 'Distrito', 'EDP_Tipo insta', 'IEP_Tipo insta', ...(comValor ? ['EDP_Valor', 'IEP_Valor'] : []), 'Correção'];
    const corpo = linhas.map(p => {
        const o = p.edp?.dadosOriginais || {};
        const fecho = o['Data Fecho'] && moment(o['Data Fecho']).isValid() ? moment(o['Data Fecho']).format('DD/MM/YYYY') : p.data ? moment(p.data).format('DD/MM/YYYY') : '';
        const valorIep = p.discrepancias.fase2.includes('cobrado_local') ? 0 : p.valorSemIva;
        return [
            p.numeroParceiro ?? '',
            p.numeroProcesso,
            fecho,
            o['Distrito'] ?? p.distrito ?? '',
            p.edp ? p.edp.tipoInstalacao ?? '' : '(não consta)',
            p.tipoInstalacaoEdp ?? p.tipoInstalacao ?? '',
            ...(comValor ? [p.edp?.valor ?? '', valorIep] : []),
            p.discrepancias.fase2.map(d => DISCREPANCIA_LABEL[d]).join(', ')
        ].join(' | ');
    });
    return [cab.join(' | '), ...corpo].join('\n');
};
/**
 * Assunto e texto do e-mail à EDP de uma fase: na 1, o resumo por tipo de discrepância;
 * na 2, a tabela das correções.
 *
 * @param {IGasReconciliacaoEdp} rec Cruzamento do período
 * @param {1|2} fase 1 = discrepâncias com o Click, 2 = correções ao ficheiro da EDP
 * @returns {{assunto: string, corpo: string}} Texto para o diálogo e o `mailto:`
 */
export const textoEmail = (rec, fase) => {
    const linhas = comDiscrepancias(rec, fase);
    const contagem = {};
    linhas.forEach(p => (fase === 1 ? p.discrepancias.fase1 : p.discrepancias.fase2).forEach(d => (contagem[d] = (contagem[d] || 0) + 1)));
    const resumo = Object.keys(contagem).map(d => `- ${DISCREPANCIA_LABEL[d]}: ${contagem[d]}`).join('\n');
    const periodo = periodoTexto(rec);
    const assunto = `Inspeções de gás — ${fase === 1 ? 'discrepâncias com o Click' : 'correções ao ficheiro da EDP'}, período ${periodo}`;
    const corpo = [
        'Bom dia,',
        '',
        fase === 1
            ? `Na conciliação das inspeções de gás do período de ${periodo} com as tarefas fechadas no Click, encontrámos ${linhas.length} processo(s) com discrepâncias:`
            : `Seguem as correções do IEP ao vosso ficheiro do período de ${periodo} (${linhas.length} processo(s)):`,
        '',
        fase === 1 ? resumo || '- Sem discrepâncias.' : tabelaCorrecoes(linhas),
        '',
        `Total de processos no período: ${rec.totais.count}.`,
        fase === 1 ? 'Segue em anexo o ficheiro com o detalhe.' : 'Segue em anexo o vosso ficheiro com as linhas corrigidas assinaladas.',
        '',
        'Com os melhores cumprimentos,'
    ].join('\n');
    return { assunto, corpo };
};
